class MappingValue<T = number> {
  constructor(public label: string, public unit: string = '',
              public mapValue: (val: T) => any = (val) => typeof val === 'number' ? +val.toFixed(2) : val,
              public getUnit: (val: T) => string = () => unit) {
  }
}

export class UnitlessMappingValue<T = number> extends MappingValue<T> {
  constructor(public label: string, public mapValue: (val: T) => any = (val) => typeof val === 'number' ? +val.toFixed(2) : val) {
    super(label, '', mapValue);
  }
}

const percentageMapping = (val: number): number => 100 * val;
const negativeMapping = (val: number): number => -val.toFixed(2);

type FormMapper = Record<string, MappingValue<any>>;
type TcoMapper = Record<string, UnitlessMappingValue>;

const formMappings: { [key: string]: string } = {
  dieselForm: 'Camión diésel',
  electricForm: 'Camión eléctrico',
  hydrogenForm: 'Camión a hidrógeno',
  assumptionsForm: 'Supuestos'
};


const dieselFormMapping: FormMapper = {
  grossWeight: new MappingValue('Peso bruto', 'ton'),
  tare: new MappingValue('tara', 'ton'),
  vehicleChange: new MappingValue('Cambio de flota', 'años'),
  dailyDistance: new MappingValue('Kilómetros diarios', 'km/día'),
  performance: new MappingValue('Rendimiento', 'km/lt'),
  averageSpeed: new MappingValue('Velocidad media', 'km/h'),
  transport: new MappingValue<string>('Transporte'),
  operatingDays: new MappingValue('Días operativos', 'días'),
  gasCost: new MappingValue('Costo gasoil', '$/lt'),
  maintenanceCost: new MappingValue('Costo mantenimiento', 'USD/año'),
  insuranceCost: new MappingValue('Costo seguro', '$/año'),
  truckPrice: new MappingValue('Precio camión', 'USD'),
};

const electricFormMapping: FormMapper = {
  eGrossWeight: new MappingValue('Peso bruto', 'ton'),
  eTare: new MappingValue('Tara', 'ton'),
  battery: new MappingValue('Batería', 'kWh'),
  uteFare: new MappingValue<string>('Tarifa UTE'),
  consumedPower: new MappingValue('Potencia contratada', 'kW'),
  chargingSchedule: new MappingValue<string>('Horario carga'),
  chargingTime: new MappingValue('Horas de carga necesarias', 'horas'),
  eInsuranceCost: new MappingValue('Costo seguro', '$/año'),
  eTruckPrice: new MappingValue('Precio camión', 'USD'),
  discounts: new MappingValue('Descuento COMAP', 'USD', val => val === 0 ? 'No participa' : +val.toFixed(2), val => val === 0 ? '' : 'USD'),
};

const hydrogenFormMapping: FormMapper = {
  hGrossWeight: new MappingValue('Peso bruto', 'ton'),
  hTare: new MappingValue('Tara', 'ton'),
  tankSize: new MappingValue('Tamaño tanque', 'kg'),
  cellPower: new MappingValue('Potencia de celda', 'kW'),
  hydrogenCost: new MappingValue('Costo hidrógeno', 'USD/kg'),
  hInsuranceCost: new MappingValue('Costo seguro', '$/año'),
  hTruckPrice: new MappingValue('Precio camión', 'USD'),
  pesoDollarConv: new MappingValue('Dólar estadounidense', '$/USD'),
  discounts: new MappingValue('Descuento COMAP', 'USD', val => val === 0 ? 'No participa' : +val.toFixed(2), val => val === 0 ? '' : 'USD'),
};

const assumptionsMapping: FormMapper = {
  inflation: new MappingValue('Inflación', '%', percentageMapping),
  discountRate: new MappingValue('Tasa de descuento', '%', percentageMapping),
  gasoil: new MappingValue('Gasoil S50', '%', percentageMapping),
  electricity: new MappingValue('Electricidad', '%', percentageMapping),
  hUpgradeRate: new MappingValue('Tasa de mejora del hidrógeno', '%', percentageMapping),
  dLifespan: new MappingValue('Vida útil diésel', 'años'),
  eLifespan: new MappingValue('Vida útil eléctrico', 'años'),
  batteryTimeWarranty: new MappingValue('Garantía batería', 'años'),
  batteryDistanceWarranty: new MappingValue('Garantía batería', 'km'),
  batteryNumberOfCycles: new MappingValue('Ciclos totales de batería'),
  chargingStationCostPerKW: new MappingValue('Puesto de carga costo kW', 'USD'),
  hLifespan: new MappingValue('Vida útil hidrógeno', 'años'),
  hInfrastructureCost: new MappingValue('Costo infraestructura de hidrógeno', 'USD/kg'),
};

const tcoMapping: TcoMapper = {
  vehiclePrice: new UnitlessMappingValue('Precio'),
  discounts: new UnitlessMappingValue('Descuentos', negativeMapping),
  fuel: new UnitlessMappingValue('Combustible'),
  maintenance: new UnitlessMappingValue('Mantenimiento'),
  residualValue: new UnitlessMappingValue('Valor residual', negativeMapping),
  batteryResidualValue: new UnitlessMappingValue('Valor residual batería', negativeMapping),
  insurance: new UnitlessMappingValue('Seguro'),
  patent: new UnitlessMappingValue('Patente'),
  infrastructure: new UnitlessMappingValue('Infraestructura'),
};

const getFormMapping = (formName: string): FormMapper => {
  if (formName.includes('diesel')) {
    return dieselFormMapping;
  }

  if (formName.includes('electric')) {
    return electricFormMapping;
  }

  if (formName.includes('hydrogen')) {
    return hydrogenFormMapping;
  }

  return assumptionsMapping;
};

export const getValue = <T, >(formName: string,
                              formKey: string): MappingValue<T> => getFormMapping(formName)[formKey] ?? new MappingValue(formKey);
export const getFormTitle = (formName: string): string => formMappings[formName];

export const getTcoKeyMapping = (key: string): UnitlessMappingValue => tcoMapping[key] ?? new UnitlessMappingValue<number>(key);
